/** Fixed-aspect virtual canvas with DPR handling + letterboxing. All drawing
 *  happens in VIRTUAL_W × VIRTUAL_H units; this module maps them to the real
 *  backing store. */
export const VIRTUAL_W = 1280
export const VIRTUAL_H = 720

export interface Viewport {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  /** CSS pixels → virtual units scale (same on both axes, aspect is fixed). */
  scale: number
  dpr: number
  cssW: number
  cssH: number
  /** Convert client (CSS px) coordinates to virtual coordinates. */
  toVirtual: (clientX: number, clientY: number) => { x: number; y: number }
  /** Reset transform so subsequent drawing is in virtual units. */
  begin: () => void
  onResize: (fn: () => void) => void
  /** 09 §6 letterbox: `fit` = bars, `fill` = crop the long axis equally. */
  setLetterbox: (mode: 'fit' | 'fill') => void
  letterbox: 'fit' | 'fill'
  /** Virtual-px offset of the 1280×720 frame inside the canvas (negative = cropped). */
  ox: number
  oy: number
  /** Visible safe area in virtual px (the whole frame under `fit`). */
  safe: () => { x0: number; y0: number; x1: number; y1: number }
}

export function createViewport(canvas: HTMLCanvasElement): Viewport {
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('2d context unavailable')
  const listeners: (() => void)[] = []
  const vp: Viewport = {
    canvas, ctx, scale: 1, dpr: 1, cssW: VIRTUAL_W, cssH: VIRTUAL_H, letterbox: 'fit', ox: 0, oy: 0,
    toVirtual: (cx, cy) => {
      const r = canvas.getBoundingClientRect()
      return { x: (cx - r.left) / vp.scale - vp.ox, y: (cy - r.top) / vp.scale - vp.oy }
    },
    begin: () => {
      const s = vp.scale * vp.dpr
      ctx.setTransform(s, 0, 0, s, vp.ox * s, vp.oy * s)
      if (vp.letterbox === 'fill') { ctx.beginPath(); ctx.rect(0, 0, VIRTUAL_W, VIRTUAL_H); ctx.clip() }
    },
    onResize: (fn) => { listeners.push(fn) },
    setLetterbox: (mode) => { if (mode !== vp.letterbox) { vp.letterbox = mode; resize() } },
    safe: () => ({ x0: Math.max(0, -vp.ox), y0: Math.max(0, -vp.oy), x1: Math.min(VIRTUAL_W, VIRTUAL_W + vp.ox), y1: Math.min(VIRTUAL_H, VIRTUAL_H + vp.oy) }),
  }

  function resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 3)
    const availW = window.innerWidth
    const availH = window.innerHeight
    const fill = vp.letterbox === 'fill'
    const scale = fill ? Math.max(availW / VIRTUAL_W, availH / VIRTUAL_H) : Math.min(availW / VIRTUAL_W, availH / VIRTUAL_H)
    // fit: the canvas is the frame (bars outside it are the page's paper). fill: the canvas is the window
    // and the frame is centred inside it, cropped equally on the long axis (09 §6).
    const cssW = fill ? availW : Math.floor(VIRTUAL_W * scale)
    const cssH = fill ? availH : Math.floor(VIRTUAL_H * scale)
    vp.ox = fill ? (cssW / scale - VIRTUAL_W) / 2 : 0
    vp.oy = fill ? (cssH / scale - VIRTUAL_H) / 2 : 0
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    canvas.width = Math.round(cssW * dpr)
    canvas.height = Math.round(cssH * dpr)
    vp.scale = scale
    vp.dpr = dpr
    vp.cssW = cssW
    vp.cssH = cssH
    for (const fn of listeners) fn()
  }
  window.addEventListener('resize', resize)
  resize()
  return vp
}
